import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { migrationReport, type CircuitState } from '@/api/client'
import { useState } from 'react'
import { AlertTriangle, CheckCircle2, Clock, ShieldAlert, RotateCcw } from 'lucide-react'
import clsx from 'clsx'

interface Props {
  projectId: string
  /** Se compact=true, mostra só o badge sem detalhes (pra header de página). Default: false */
  compact?: boolean
}

export function CircuitBreakerBadge({ projectId, compact = false }: Props) {
  const qc = useQueryClient()
  const [confirming, setConfirming] = useState(false)

  const { data: circuit } = useQuery({
    queryKey: ['circuit-state', projectId],
    queryFn: () => migrationReport.circuitState(projectId),
    enabled: !!projectId,
    refetchInterval: 5000,
  })

  const forceCloseMut = useMutation({
    mutationFn: (reason: string) => migrationReport.forceCloseCircuit(projectId, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['circuit-state', projectId] })
      setConfirming(false)
    },
  })

  if (!circuit) return null

  const isOpen = circuit.state === 'Open'
  const isHalfOpen = circuit.state === 'HalfOpen'
  const isClosed = circuit.state === 'Closed'

  // Sprint 4f Bug C: não mostra badge quando Closed (estado normal/operacional)
  if (isClosed) return null

  // Compacto: só badge colorido se aberto/half-open, nada se fechado
  if (compact) {
    if (isClosed) return null
    return (
      <div className={clsx(
        'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium',
        isOpen ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800',
      )}>
        {isOpen ? <ShieldAlert className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
        {isOpen ? 'Pausado automaticamente' : 'Aguardando recuperação'}
      </div>
    )
  }

  if (isClosed) {
    return (
      <div className="rounded-md border border-green-200 bg-green-50 p-3 flex items-center gap-2">
        <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
        <div className="text-sm text-green-900">
          Circuit Breaker fechado — operação normal
        </div>
      </div>
    )
  }

  const nextProbeIn = circuit.nextProbeAt
    ? Math.max(0, Math.floor((new Date(circuit.nextProbeAt).getTime() - Date.now()) / 1000))
    : 0

  function formatDuration(s: number): string {
    if (s <= 0) return 'agora'
    const m = Math.floor(s / 60)
    const sec = s % 60
    if (m > 0) return `${m}min ${sec}s`
    return `${sec}s`
  }

  return (
    <div className={clsx(
      'rounded-md border p-4',
      isOpen ? 'border-red-200 bg-red-50' : 'border-amber-200 bg-amber-50',
    )}>
      <div className="flex items-start gap-3">
        {isOpen ? (
          <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
        ) : (
          <Clock className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
        )}
        <div className="flex-1 min-w-0">
          <div className={clsx(
            'font-semibold mb-1',
            isOpen ? 'text-red-900' : 'text-amber-900',
          )}>
            {isOpen ? 'Migração pausada automaticamente' : 'Tentando recuperação...'}
          </div>
          <div className="text-sm text-slate-700 space-y-1">
            {circuit.openReason && (
              <div>
                <span className="text-slate-500">Motivo:</span>{' '}
                <span className="font-medium">{circuit.openReason}</span>
              </div>
            )}
            {circuit.openedAt && (
              <div>
                <span className="text-slate-500">Pausado desde:</span>{' '}
                <span className="font-medium">{new Date(circuit.openedAt).toLocaleTimeString()}</span>
              </div>
            )}
            {nextProbeIn > 0 && (
              <div>
                <span className="text-slate-500">Próxima tentativa em:</span>{' '}
                <span className="font-medium">{formatDuration(nextProbeIn)}</span>
              </div>
            )}
          </div>

          <div className="mt-3 flex gap-2">
            {!confirming ? (
              <button
                type="button"
                onClick={() => setConfirming(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded border border-slate-300 bg-white hover:bg-slate-50 text-slate-700"
              >
                <RotateCcw className="h-3 w-3" />
                Forçar retomada
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-600">Tem certeza?</span>
                <button
                  type="button"
                  disabled={forceCloseMut.isPending}
                  onClick={() => forceCloseMut.mutate('Override manual via UI')}
                  className="px-3 py-1.5 text-xs font-medium rounded bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
                >
                  {forceCloseMut.isPending ? 'Forçando...' : 'Sim, forçar'}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirming(false)}
                  className="px-3 py-1.5 text-xs font-medium rounded border border-slate-300 bg-white hover:bg-slate-50 text-slate-700"
                >
                  Cancelar
                </button>
              </div>
            )}
          </div>

          {forceCloseMut.isError && (
            <div className="text-xs text-red-600 mt-2">
              Erro ao forçar retomada. Tente novamente.
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
